/** @file sectorPick: an azimuth looked up in a sector table: sector, tile, flip, sideBack, drawn angles. */
import { sectorCentre } from '@zilo/pose3d-format/tables/sectorCentre';
import { sectorOf } from '@zilo/pose3d-format/tables/sectorOf';
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import { wrapDegrees } from '@zilo/pose3d-std/math/wrapDegrees';

/** The lookup result: sector index, drawn tile (-1 = nothing), mirror flag, sideBack class and the drawn angles. */
export type SectorPickOut = {
  sector: number; tile: number; flip: boolean; sideBack: 0 | 1 | 2; drawnAzimuthDeg: number; drawnScreenAngleDeg: number;
};

/** The sector's centre; 0 for a one-sector table (its whole-circle sector would centre on 180, behind the puppet). */
function nominalAzimuth(table: SectorTable, sector: number): number {
  return table.thresholds.length === 1 ? 0 : sectorCentre(table, sector);
}

/**
 * Plan decision 14: `sector = sectorOf(azimuthDeg, table)`, `tile`/`flip`/`sideBack` from the table's columns
 * (`extendX` changes only the rect). Drawn angles of the tile: `entry.drawnAzimuths[tile]` (absent: the sector's
 * nominal centre, which already lies on the drawn side) and `entry.drawnScreenAngles[tile]` (absent: 0), each
 * `wrapDegrees(360 - a)` when flipped; both 0 for tile -1. Pass the FLIP twin as `table` when the entry folds.
 */
export function sectorPick(table: SectorTable, entry: FacetEntry, azimuthDeg: number, out: SectorPickOut): SectorPickOut {
  const sector = sectorOf(azimuthDeg, table);
  const tile = table.tile[sector] ?? -1;
  const flip = table.flip[sector] === true;
  out.sector = sector;
  out.tile = tile;
  out.flip = flip;
  out.sideBack = table.sideBack[sector] ?? 0;
  if (tile < 0) {
    out.drawnAzimuthDeg = 0;
    out.drawnScreenAngleDeg = 0;
    return out;
  }
  const azimuth = entry.drawnAzimuths?.[tile];
  const screen = entry.drawnScreenAngles?.[tile] ?? 0;
  out.drawnAzimuthDeg = azimuth === undefined ? nominalAzimuth(table, sector) : flip ? wrapDegrees(360 - azimuth) : azimuth;
  out.drawnScreenAngleDeg = flip ? wrapDegrees(360 - screen) : screen;
  return out;
}
